import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast } from './listPageStyles';

const API_BASE = '/api/field-dashboard/product-breakdown';

const RANGE_TABS = [
  { key: 'today', label: 'Bugün' },
  { key: 'week', label: 'Son 7 Gün' },
  { key: 'month', label: 'Son 30 Gün' },
  { key: 'all', label: 'Tümü' },
];

// Donut/legend renk paleti.
const PALETTE = ['#0073ea', '#16a34a', '#fdab3d', '#8b5cf6', '#0d9488', '#ec4899', '#e2445c', '#64748b'];

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrap(payload) {
  return pick(payload, 'Data', 'data') ?? payload ?? {};
}

function money(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '₺0';
  return '₺' + n.toLocaleString('tr-TR', { maximumFractionDigits: 0 });
}

function num(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('tr-TR', { maximumFractionDigits: 0 });
}

function rangeToParams(key) {
  if (key === 'all') return { from: '', to: '' };
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const to = new Date(todayStart); to.setDate(to.getDate() + 1);
  const from = new Date(todayStart);
  if (key === 'week') from.setDate(from.getDate() - 6);
  else if (key === 'month') from.setDate(from.getDate() - 29);
  return { from: from.toISOString(), to: to.toISOString() };
}

// Top N + kalanı "Diğer" altında topla (donut legend'ı sade kalsın).
function collapseTop(items, valueKey, labelKey, topN) {
  const sorted = [...items].sort((a, b) => (Number(pick(b, valueKey)) || 0) - (Number(pick(a, valueKey)) || 0));
  const head = sorted.slice(0, topN);
  const rest = sorted.slice(topN);
  const out = head.map(x => ({ label: pick(x, labelKey) || 'Diğer', value: Number(pick(x, valueKey)) || 0 }));
  if (rest.length > 0) {
    const restSum = rest.reduce((a, x) => a + (Number(pick(x, valueKey)) || 0), 0);
    if (restSum > 0) out.push({ label: `Diğer (${rest.length})`, value: restSum });
  }
  return out;
}

function Donut({ segments, size = 160 }) {
  const total = segments.reduce((a, x) => a + x.value, 0);
  const r = size / 2 - 14;
  const cx = size / 2, cy = size / 2;
  const circ = 2 * Math.PI * r;
  if (total <= 0) {
    return <div style={{ width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>Veri yok</div>;
  }
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <g transform={`rotate(-90 ${cx} ${cy})`}>
        {segments.map((seg, i) => {
          const frac = seg.value / total;
          const dash = frac * circ;
          const el = (
            <circle key={i} cx={cx} cy={cy} r={r} fill="none"
              stroke={PALETTE[i % PALETTE.length]} strokeWidth="20"
              strokeDasharray={`${dash} ${circ - dash}`} strokeDashoffset={-offset} />
          );
          offset += dash;
          return el;
        })}
      </g>
      <text x={cx} y={cy - 4} textAnchor="middle" style={{ fontSize: 13, fontWeight: 800, fill: '#0f172a' }}>{money(total)}</text>
      <text x={cx} y={cy + 14} textAnchor="middle" style={{ fontSize: 10, fill: '#94a3b8' }}>toplam</text>
    </svg>
  );
}

function DonutCard({ title, segments }) {
  const total = segments.reduce((a, x) => a + x.value, 0);
  return (
    <div style={{ flex: '1 1 360px', minWidth: 320, border: '1px solid #e5e7eb', borderRadius: 14, padding: 16, background: '#fff' }}>
      <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', marginBottom: 12 }}>{title}</div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <Donut segments={segments} />
        <div style={{ flex: 1, minWidth: 150, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {segments.length === 0 && <span style={{ color: '#94a3b8', fontSize: 13 }}>Veri yok</span>}
          {segments.map((seg, i) => {
            const pct = total > 0 ? Math.round((seg.value / total) * 100) : 0;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: PALETTE[i % PALETTE.length], flexShrink: 0 }} />
                <span style={{ flex: 1, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{seg.label}</span>
                <span style={{ color: '#94a3b8' }}>%{pct}</span>
                <span style={{ fontWeight: 700, color: '#0f172a', minWidth: 70, textAlign: 'right' }}>{money(seg.value)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, tone }) {
  const tones = {
    blue: { color: '#1d4ed8', dot: 'var(--accent)', bg: 'rgba(0,115,234,0.06)', border: 'rgba(0,115,234,0.22)' },
    green: { color: '#0f5132', dot: '#16a34a', bg: 'rgba(0,200,117,0.07)', border: 'rgba(0,200,117,0.24)' },
    purple: { color: '#5b21b6', dot: '#8b5cf6', bg: 'rgba(139,92,246,0.07)', border: 'rgba(139,92,246,0.24)' },
  };
  const t = tones[tone] || tones.blue;
  return (
    <div style={{ flex: '1 1 180px', minWidth: 170, border: `1px solid ${t.border}`, borderRadius: 14, padding: 16, background: t.bg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: t.color }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />{label}
      </div>
      <div style={{ fontWeight: 900, fontSize: 26, color: '#0f172a', marginTop: 8, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, fontWeight: 600 }}>{sub}</div>}
    </div>
  );
}

export function SalesAnalyticsPage() {
  const [range, setRange] = useState('month');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { from, to } = rangeToParams(range);
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      params.set('top', '12');
      const qs = params.toString();
      const res = await fetch(`${API_BASE}${qs ? `?${qs}` : ''}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Analiz alınamadı (${res.status})`);
      const payload = await res.json();
      setData(unwrap(payload));
    } catch (err) {
      showToast('error', err.message || 'Analiz alınamadı');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range, showToast]);

  useEffect(() => { load(); }, [load]);

  const byProduct = pick(data, 'byProduct', 'ByProduct') ?? [];
  const producerSegments = useMemo(
    () => collapseTop(pick(data, 'byProducer', 'ByProducer') ?? [], 'revenue', 'producerName', 7),
    [data]);
  const groupSegments = useMemo(
    () => collapseTop(pick(data, 'byGroup', 'ByGroup') ?? [], 'revenue', 'groupName', 7),
    [data]);
  const hourly = pick(data, 'hourly', 'Hourly') ?? [];

  const totalRevenue = pick(data, 'totalRevenue', 'TotalRevenue') ?? 0;
  const productCount = pick(data, 'productCount', 'ProductCount') ?? 0;
  const lineCount = pick(data, 'lineCount', 'LineCount') ?? 0;
  const visitCount = pick(data, 'visitCount', 'VisitCount') ?? 0;

  const maxProductRevenue = byProduct.reduce((m, p) => Math.max(m, Number(pick(p, 'revenue', 'Revenue')) || 0), 0) || 1;
  const maxHour = hourly.reduce((m, h) => Math.max(m, Number(pick(h, 'count', 'Count')) || 0), 0) || 1;
  // İş saatleri penceresi (7-20) — gece saatlerini gizle, grafiği sıkıştırma.
  const hourWindow = hourly.filter(h => { const hr = Number(pick(h, 'hour', 'Hour')); return hr >= 7 && hr <= 20; });

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>Satış Analizi</h1>
          <p style={s.pageSubtitle}>
            Sipariş satırlarından ciro bazlı top ürünler, marka ve ürün grubu dağılımı,
            saatlik ziyaret yoğunluğu. (Mobil Gün Özeti grafiklerinin gerçek-veri web muadili.)
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {RANGE_TABS.map(tab => (
            <button key={tab.key} type="button" onClick={() => setRange(tab.key)}
              style={{ ...(range === tab.key ? s.btnPrimary : s.btnSecondary), padding: '6px 14px', fontSize: 13 }}>
              {tab.label}
            </button>
          ))}
          <button type="button" style={s.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 18 }}>
        <KpiCard tone="green" label="Toplam Ciro" value={money(totalRevenue)} sub={`${num(lineCount)} satır`} />
        <KpiCard tone="blue" label="Farklı Ürün" value={num(productCount)} sub="satışı olan" />
        <KpiCard tone="purple" label="Ziyaret" value={num(visitCount)} sub="dönem içi" />
      </div>

      {/* Top ürünler */}
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 14, padding: 16, background: '#fff', marginBottom: 18 }}>
        <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', marginBottom: 12 }}>Top Ürünler (ciro)</div>
        {byProduct.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13, padding: 16, textAlign: 'center' }}>Bu dönemde satış yok</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {byProduct.map((p, i) => {
              const rev = Number(pick(p, 'revenue', 'Revenue')) || 0;
              const pct = Math.round((rev / maxProductRevenue) * 100);
              return (
                <div key={pick(p, 'productId', 'ProductId') || i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 220, minWidth: 220, fontSize: 13, fontWeight: 600, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {pick(p, 'productName', 'ProductName') || '-'}
                  </div>
                  <div style={{ flex: 1, height: 22, borderRadius: 6, background: '#f1f5f9', overflow: 'hidden', position: 'relative' }}>
                    <div style={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(90deg,var(--accent),#1d4ed8)', borderRadius: 6 }} />
                  </div>
                  <div style={{ width: 100, textAlign: 'right', fontWeight: 700, fontSize: 13, color: '#0f172a' }}>{money(rev)}</div>
                  <div style={{ width: 70, textAlign: 'right', fontSize: 12, color: '#94a3b8' }}>{num(pick(p, 'quantity', 'Quantity'))} ad</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Marka + grup donut */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 18 }}>
        <DonutCard title="Marka Dağılımı (Üretici)" segments={producerSegments} />
        <DonutCard title="Ürün Grubu Dağılımı" segments={groupSegments} />
      </div>

      {/* Saatlik ziyaret */}
      <div style={{ border: '1px solid #e5e7eb', borderRadius: 14, padding: 16, background: '#fff' }}>
        <div style={{ fontWeight: 800, fontSize: 15, color: '#0f172a', marginBottom: 12 }}>Saatlik Ziyaret Yoğunluğu</div>
        {visitCount === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: 13, padding: 16, textAlign: 'center' }}>Bu dönemde ziyaret yok</div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 140 }}>
            {hourWindow.map((h, i) => {
              const c = Number(pick(h, 'count', 'Count')) || 0;
              const hr = Number(pick(h, 'hour', 'Hour'));
              const barH = Math.round((c / maxHour) * 110);
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700 }}>{c || ''}</span>
                  <div title={`${hr}:00 — ${c} ziyaret`} style={{ width: '100%', maxWidth: 28, height: Math.max(2, barH), background: c > 0 ? 'linear-gradient(180deg,#16a34a,#0f5132)' : '#e5e7eb', borderRadius: 4 }} />
                  <span style={{ fontSize: 10, color: '#64748b' }}>{hr}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Toast toast={toast} />
    </div>
  );
}

export default SalesAnalyticsPage;
